// Catches the #id_token fragment that Google Identity Services injects after
// the BYO Google login popup, exchanges it for our customer JWT, then redirects home.
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { formatApiErrorDetail } from '../../lib/api';

const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { loginWithGoogleSession } = useCustomerAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    // BYO flow: GSI puts the Google-signed JWT in the fragment as #id_token=...
    // Legacy fallback: older flow used #session_id=...
    const hash = window.location.hash || '';
    const idTokenMatch = hash.match(/id_token=([^&]+)/);
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    const payload = idTokenMatch
      ? { id_token: decodeURIComponent(idTokenMatch[1]) }
      : sessionIdMatch
        ? { session_id: decodeURIComponent(sessionIdMatch[1]) }
        : null;

    if (!payload) {
      setError('Sign-in cancelled or no Google token received');
      return;
    }
    loginWithGoogleSession(payload)
      .then(() => {
        window.history.replaceState({}, '', '/');
        navigate('/', { replace: true });
      })
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Google sign-in failed'));
  }, [navigate, loginWithGoogleSession, params]);

  return (
    <div className="min-h-screen grid place-items-center bg-gray-50 dark:bg-gray-950 px-4" data-testid="auth-callback">
      {!error ? (
        <div className="flex flex-col items-center gap-3 text-gray-700 dark:text-gray-300">
          <Loader2 className="h-8 w-8 animate-spin text-red-600" />
          <p className="text-sm font-semibold">Finishing sign-in…</p>
        </div>
      ) : (
        <div className="max-w-sm w-full rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/40 p-5 text-center">
          <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-2" />
          <p className="text-sm font-bold text-red-800 dark:text-red-300">Sign-in failed</p>
          <p className="text-xs text-red-700 dark:text-red-400 mt-1">{error}</p>
          <button onClick={() => navigate('/', { replace: true })} className="mt-4 text-sm font-semibold text-red-700 underline">Go home</button>
        </div>
      )}
    </div>
  );
};

export default AuthCallbackPage;
